import styled from 'styled-components';

import { Loader } from 'components/Loader';

import { SectionContainer } from 'components/DetailsView/Section';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

import {
    StockVariationFragmentFragment,
    StockVariationType,
    useGetAllProductsQuery,
    useGetColumnsQuery,
    useGetSitesQuery,
    useGetStockVariationsForDateQuery,
} from 'data/__generated__';
import { StockVariationList } from '../StockVariationList';
import { siteVariationsLabels } from '../constants';

export const SiteVariationsList = ({ date }: { date: Date }) => {
    const {
        data: stockVariationsData,
        error: stockVariationsError,
        loading: stockVariationsLoading,
    } = useGetStockVariationsForDateQuery({
        fetchPolicy: 'cache-and-network',
        variables: { date: formatDateAsAnniversary({ dateTime: date, useNewFormat: true }) },
    });

    const { loading: productsLoading, data: productsData, error: productsError } = useGetAllProductsQuery();

    const { loading: sitesLoading, data: sitesData, error: sitesError } = useGetSitesQuery();

    // Les StockVariation sont désormais clés par colonne (stock v3) : on récupère les colonnes pour
    // résoudre la colonne -> son site et regrouper les variations par site comme avant.
    const { loading: columnsLoading, data: columnsData, error: columnsError } = useGetColumnsQuery();

    if (
        (stockVariationsLoading && !stockVariationsData) ||
        (sitesLoading && !sitesData) ||
        (productsLoading && !productsData) ||
        (columnsLoading && !columnsData)
    ) {
        return (
            <Content>
                <Loader />
            </Content>
        );
    }

    if (
        stockVariationsError ||
        !stockVariationsData ||
        sitesError ||
        !sitesData ||
        productsError ||
        !productsData ||
        columnsError ||
        !columnsData
    ) {
        throw new Error('Une erreur est survenue lors de la récupération des variations');
    }

    function mapVariationsByProductId(
        variations: StockVariationFragmentFragment[],
    ): Map<string, StockVariationFragmentFragment[]> {
        const variationsByProductId: Map<string, StockVariationFragmentFragment[]> = new Map();
        variations.forEach((variation) =>
            variationsByProductId.set(variation.productId, [
                ...(variationsByProductId.get(variation.productId) || []),
                variation,
            ]),
        );

        return variationsByProductId;
    }

    function displayVariationsByProduct(variations: StockVariationFragmentFragment[]) {
        const variationsByProductId = mapVariationsByProductId(variations);
        return Array.from(variationsByProductId.keys()).map((productId) => {
            const product = products.find(({ _id }) => _id === productId);
            const productVariations = variationsByProductId.get(productId) || [];
            return (
                <ProductVariationContainer key={productId}>
                    <ProductName>
                        {product ? `${product.name} - ${product.brand} - ${product.volume}` : productId}
                    </ProductName>
                    <StockVariationList variations={productVariations} />
                </ProductVariationContainer>
            );
        });
    }

    const { sites } = sitesData;
    const { getStockVariationsForDate: stockVariations } = stockVariationsData;
    const { getAllProducts: products } = productsData;
    const { columns } = columnsData;

    const siteIdByColumnId = new Map(columns.map((column) => [column._id, column.siteId]));

    return (
        <Content>
            {sites.map((site) => {
                const siteStockVariations = stockVariations.filter(
                    ({ columnId }) => siteIdByColumnId.get(columnId) === site._id,
                );
                return (
                    <SectionContainer
                        title={`${site.name} (${siteStockVariations.length} variations)`}
                        isInitiallyOpen={false}
                        key={site._id}
                    >
                        <>
                            <VariationsDetails>
                                <VariationsSummary>Toutes les variations du jour</VariationsSummary>
                                {displayVariationsByProduct(siteStockVariations)}
                            </VariationsDetails>
                            {Object.values(StockVariationType).map((stockVariationType: StockVariationType) => {
                                const typeVariations = siteStockVariations.filter(
                                    ({ type }) => type === stockVariationType,
                                );

                                if (!typeVariations.length) {
                                    return null;
                                }
                                return (
                                    <VariationsDetails key={stockVariationType}>
                                        <VariationsSummary>
                                            {siteVariationsLabels[stockVariationType]}
                                        </VariationsSummary>
                                        {displayVariationsByProduct(typeVariations)}
                                    </VariationsDetails>
                                );
                            })}
                        </>
                    </SectionContainer>
                );
            })}
        </Content>
    );
};

const Content = styled.div`
    padding: 15px;
    flex: 1;
    overflow: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const VariationsDetails = styled.details`
    margin-top: 5px;
`;

const VariationsSummary = styled.summary`
    cursor: pointer;
    font-weight: bold;
`;

const ProductVariationContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 10px;
    align-items: center;
`;
const ProductName = styled.div`
    font-weight: bold;
    width: 600px;
    overflow: hidden;
`;
